import { IconButton, MenuItem, Stack, TextField } from "@mui/material";

import { defaultOp, humanise, kindOf, opLabel, opTakesValue, opsFor } from "../lib/spec.js";

/**
 * One filter, as a sentence a person edits: field, operator, value.
 *
 * Used by the builder for a chart's own filters and by a dashboard for the ones
 * it shares, so the two read and behave the same. `fields` is what may be named
 * (the caller has already left out what the kernel would refuse), and a value
 * is held as the text typed, since turning it into what the field is happens
 * when the spec is built and not on every keystroke.
 */
export default function FilterRow({ f, fields, onChange, onRemove, label = "Where" }) {
  const field = fields.find((x) => x.name === f.field);
  const ops = field ? opsFor(field) : [];
  const enumValues = field?.enum && (f.op === "eq" || f.op === "ne") ? field.enum : null;
  return (
    <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
      <TextField
        select size="small" label={label} value={f.field}
        onChange={(e) => {
          const next = fields.find((x) => x.name === e.target.value);
          onChange({ field: e.target.value, op: next ? defaultOp(next) : "eq", value: "", unit: undefined });
        }}
        sx={{ minWidth: 220 }}
      >
        {fields.map((x) => <MenuItem key={`${x.name}:${kindOf(x)}`} value={x.name}>{humanise(x.name)}</MenuItem>)}
      </TextField>
      <TextField
        select size="small" label="Is" value={ops.includes(f.op) ? f.op : ops[0] || ""}
        onChange={(e) => onChange({ op: e.target.value })} sx={{ minWidth: 170 }}
      >
        {ops.map((op) => <MenuItem key={op} value={op}>{opLabel(field, op)}</MenuItem>)}
      </TextField>
      {opTakesValue(f.op) ? (
        enumValues ? (
          <TextField
            select size="small" label="Value" value={f.value} onChange={(e) => onChange({ value: e.target.value })}
            sx={{ minWidth: 160 }}
          >
            {enumValues.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
        ) : (
          <TextField
            size="small" label="Value" value={f.value}
            onChange={(e) => onChange({ value: e.target.value, ...(field?.minor_units ? { unit: "major" } : {}) })}
            placeholder={field && kindOf(field) === "time" ? "2026-09-30"
              : f.op === "in" ? "a, b, c"
              : field?.minor_units ? "10k" : ""}
            helperText={!field?.minor_units ? undefined
              : f.unit === "major" || f.value === "" ? "As you would say it: 10000, 10,000 or 10k. The currency sets the cents."
              : "Saved in minor units (cents). Type it again in whole units to change it."}
            sx={{ minWidth: 200 }}
          />
        )
      ) : null}
      <IconButton aria-label="Remove this filter" size="small" onClick={onRemove}>×</IconButton>
    </Stack>
  );
}
